class DetailsDisclosure extends HTMLElement {
    constructor() {
        super();
        this.mainDetailsToggle = this.querySelector('details');

        if (!this.mainDetailsToggle) {
            console.warn('DetailsDisclosure: No <details> element found in the component.');
            return;
        }

        this.content = this.mainDetailsToggle.querySelector('div, ul, .disclosure-content');
        this.summary = this.mainDetailsToggle.querySelector('summary');
        this.isAnimating = false;
        
        // Bind methods to preserve context
        this.handleSummaryClick = this.handleSummaryClick.bind(this);
        this.onKeyUp = this.onKeyUp.bind(this);
        this.onFocusOut = this.onFocusOut.bind(this);
        
        // Override default details toggle behavior
        if (this.summary) {
            this.summary.addEventListener('click', this.handleSummaryClick);
        }
        
        this.addEventListener('keyup', this.onKeyUp);
        document.addEventListener('click', this.onFocusOut);
        
        // Initialize content styles for animation
        // Use setTimeout to ensure DOM is fully ready
        setTimeout(() => {
            this.initializeStyles();
        }, 0);
    }

    disconnectedCallback() {
        // Clean up event listeners when component is removed
        if (this.summary) {
            this.summary.removeEventListener('click', this.handleSummaryClick);
        }
        
        this.removeEventListener('keyup', this.onKeyUp);
        document.removeEventListener('click', this.onFocusOut);
    }

    onKeyUp(event) {
        if (!this.mainDetailsToggle) return; // Ensure element exists
        if (event.code.toUpperCase() !== 'ESCAPE') return;

        const openDetailsElement = event.target.closest('details[open]');
        if (!openDetailsElement) return;

        const summaryElement = openDetailsElement.querySelector('summary');
        this.closeWithAnimation();
        summaryElement.focus();
    }

    onFocusOut(e) {
        if (!this.mainDetailsToggle) return; // Ensure element exists

        const withBoundaries = e.composedPath().includes(this.mainDetailsToggle);

        if (!withBoundaries) {
            this.closeWithAnimation();
        }
    }

    close() {
        if (!this.mainDetailsToggle) return; // Ensure element exists
        this.closeWithAnimation();
    }

    initializeStyles() {
        if (!this.content) {
            console.warn('DetailsDisclosure: No content element found for animation');
            return;
        }
        
        // Set up CSS custom properties for smooth transitions
        this.content.style.overflow = 'hidden';
        this.content.style.transition = 'transform 0.3s ease-out, opacity 0.3s ease-out';
        
        // Force the content to be visible for measurement
        this.content.style.display = 'block';
        
        // Set initial state based on current open state
        if (!this.mainDetailsToggle.hasAttribute('open')) {
            // Closed state - ensure content is hidden
            this.content.style.transform = 'translateY(-1.5rem)';
            this.content.style.opacity = '0';
        } else {
            // Open state - ensure content is visible
            this.content.style.transform = 'translateY(0)';
            this.content.style.opacity = '1';
        }
        
        // Force reflow to apply styles
        this.content.offsetHeight;
    }

    handleSummaryClick(event) {
        if (this.isAnimating) {
            event.preventDefault();
            return;
        }

        // Prevent default toggle behavior to handle animation
        event.preventDefault();
        
        const isCurrentlyOpen = this.mainDetailsToggle.hasAttribute('open');
        
        if (isCurrentlyOpen) {
            this.closeWithAnimation();
        } else {
            this.openWithAnimation();
        }
    }

    openWithAnimation() {
        if (this.isAnimating || !this.content) return;
        
        this.isAnimating = true;
        
        // Close any open HeaderCollapsiblePanel instances (menu drawers)
        this.closeHeaderCollapsiblePanels();
        
        // Ensure details is open for display
        this.mainDetailsToggle.setAttribute('open', '');
        
        // Ensure content is visible
        this.content.style.display = 'block';
        
        // Start from slid up state - force the starting position
        this.content.style.transform = 'translateY(-1.5rem)';
        this.content.style.opacity = '0';
        
        // Force reflow to ensure starting position is applied
        this.content.offsetHeight;
        
        // Use double requestAnimationFrame for better browser compatibility
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                // Animate to slide down position
                this.content.style.transform = 'translateY(0)';
                this.content.style.opacity = '1';
            });
        });
        
        // Clean up after animation
        setTimeout(() => {
            this.isAnimating = false;
        }, 350);
    }

    closeWithAnimation() {
        if (this.isAnimating || !this.content) return;
        
        this.isAnimating = true;
        
        // Start from visible position - ensure starting state
        this.content.style.transform = 'translateY(0)';
        this.content.style.opacity = '1';
        
        // Force reflow to ensure starting position is applied
        this.content.offsetHeight;
        
        // Use double requestAnimationFrame for better browser compatibility
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                // Animate to slide up position
                this.content.style.transform = 'translateY(-1.5rem)';
                this.content.style.opacity = '0';
            });
        });
        
        // Remove open attribute after animation
        setTimeout(() => {
            this.mainDetailsToggle.removeAttribute('open');
            this.isAnimating = false;
        }, 350);
    }

    closeHeaderCollapsiblePanels() {
        // Check if HeaderCollapsiblePanel class exists and has instances
        if (typeof HeaderCollapsiblePanel !== 'undefined' && HeaderCollapsiblePanel.instances) {
            HeaderCollapsiblePanel.instances.forEach(instance => {
                if (instance.isOpen()) {
                    instance.close();
                }
            });
        }
    }
}

customElements.define('details-disclosure', DetailsDisclosure);
